import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { anlagenAPI, wartungenAPI } from '../services/api';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import Navbar from '../components/Navbar';

const WartungForm = () => {
  const { anlageId } = useParams();
  const navigate = useNavigate();

  const [anlage, setAnlage] = useState(null);
  const [formData, setFormData] = useState({
    wartungsdatum: new Date().toISOString().slice(0, 16),
    durchgefuehrte_arbeiten: '',
    betriebsstunden: '',
    maengel: ''
  });
  
  const [messwerte, setMesswerte] = useState([{ key: '', value: '' }]);
  const [austauschteile, setAustauschteile] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnlage();
  }, [anlageId]);

  const loadAnlage = async () => {
    try {
      const data = await anlagenAPI.getById(anlageId);
      setAnlage(data);
    } catch (error) {
      console.error('Fehler beim Laden der Anlage:', error);
      setError('Anlage konnte nicht geladen werden');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMesswertChange = (index, field, value) => {
    const newMesswerte = [...messwerte];
    newMesswerte[index][field] = value;
    setMesswerte(newMesswerte);
  };

  const addMesswert = () => {
    setMesswerte([...messwerte, { key: '', value: '' }]);
  };

  const removeMesswert = (index) => {
    setMesswerte(messwerte.filter((_, i) => i !== index));
  };

  const handleAustauschteilChange = (index, value) => {
    const newTeile = [...austauschteile];
    newTeile[index] = value;
    setAustauschteile(newTeile);
  };

  const addAustauschteil = () => {
    setAustauschteile([...austauschteile, '']);
  };

  const removeAustauschteil = (index) => {
    setAustauschteile(austauschteile.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const messwertObj = {};
      messwerte.forEach(m => {
        if (m.key && m.value) {
          messwertObj[m.key] = m.value;
        }
      });

      const teileFiltered = austauschteile.filter(t => t.trim() !== '');

      const wartungData = {
        anlage_id: anlageId,
        wartungsdatum: formData.wartungsdatum,
        durchgefuehrte_arbeiten: formData.durchgefuehrte_arbeiten,
        betriebsstunden: formData.betriebsstunden ? parseInt(formData.betriebsstunden) : null,
        messwerte: messwertObj,
        austauschteile: teileFiltered,
        maengel: formData.maengel || null
      };

      await wartungenAPI.create(wartungData);
      navigate(`/anlage/${anlageId}`);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError(error.response?.data?.detail || 'Fehler beim Speichern der Wartung');
    } finally {
      setLoading(false);
    }
  };

  if (!anlage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate(`/anlage/${anlageId}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück zur Anlage
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="form-title">Neue Wartung dokumentieren</h1>
            <p className="text-gray-600">
              Für: {anlage.hersteller} {anlage.modell} (SN: {anlage.seriennummer})
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wartungsdatum und -uhrzeit *
              </label>
              <input
                type="datetime-local"
                name="wartungsdatum"
                value={formData.wartungsdatum}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="wartungsdatum-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Betriebsstunden
              </label>
              <input
                type="number"
                name="betriebsstunden"
                value={formData.betriebsstunden}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="z.B. 1250"
                data-testid="betriebsstunden-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durchgeführte Arbeiten *
              </label>
              <textarea
                name="durchgefuehrte_arbeiten"
                value={formData.durchgefuehrte_arbeiten}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Beschreiben Sie die durchgeführten Wartungsarbeiten..."
                data-testid="arbeiten-textarea"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Messwerte
                </label>
                <button
                  type="button"
                  onClick={addMesswert}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                  data-testid="add-messwert-button"
                >
                  <Plus className="w-4 h-4" />
                  Messwert hinzufügen
                </button>
              </div>
              <div className="space-y-2">
                {messwerte.map((messwert, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={messwert.key}
                      onChange={(e) => handleMesswertChange(index, 'key', e.target.value)}
                      placeholder="Bezeichnung (z.B. Temperatur)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid={`messwert-key-${index}`}
                    />
                    <input
                      type="text"
                      value={messwert.value}
                      onChange={(e) => handleMesswertChange(index, 'value', e.target.value)}
                      placeholder="Wert (z.B. 22°C)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid={`messwert-value-${index}`}
                    />
                    {messwerte.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMesswert(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Austauschteile / Materialien
                </label>
                <button
                  type="button"
                  onClick={addAustauschteil}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                  data-testid="add-teil-button"
                >
                  <Plus className="w-4 h-4" />
                  Teil hinzufügen
                </button>
              </div>
              <div className="space-y-2">
                {austauschteile.map((teil, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={teil}
                      onChange={(e) => handleAustauschteilChange(index, e.target.value)}
                      placeholder="z.B. Filter, Dichtung, Öl..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid={`teil-${index}`}
                    />
                    {austauschteile.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAustauschteil(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mängel / Reparaturen
              </label>
              <textarea
                name="maengel"
                value={formData.maengel}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Beschreiben Sie festgestellte Mängel oder durchgeführte Reparaturen..."
                data-testid="maengel-textarea"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition disabled:opacity-50"
                data-testid="submit-button"
              >
                <Save className="w-5 h-5" />
                {loading ? 'Speichern...' : 'Wartung dokumentieren'}
              </button>
              
              <button
                type="button"
                onClick={() => navigate(`/anlage/${anlageId}`)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WartungForm;